import React, { memo, useMemo } from 'react';
import MoreHoriz from '@/components/Icons/MoreHoriz';
import Button from '@/shared/Button';
import Div from '@/shared/Div';
import ArrowBack from '@/components/Icons/ArrowBack';
import ArrowForward from '@/components/Icons/ArrowForward';

interface PaginationConfig {
  siblingCount?: number;
  boundaryCount?: number;
}

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  blogsPerPage: number;
  totalBlogs: number;
  onPageChange: (page: number) => void;
  paginationConfig?: PaginationConfig;
}

const DEFAULT_CONFIG: Required<PaginationConfig> = {
  siblingCount: 2,
  boundaryCount: 1,
};

const Pagination: React.FC<PaginationProps> = (props: PaginationProps) => {
  const { currentPage, blogsPerPage, totalBlogs, onPageChange, paginationConfig = {} } = props;

  const config = useMemo(
    () => ({
      ...DEFAULT_CONFIG,
      ...paginationConfig,
    }),
    [paginationConfig],
  );

  const calculatedTotalPages = useMemo(() => {
    return Math.ceil(totalBlogs / blogsPerPage);
  }, [totalBlogs, blogsPerPage]);

  const currentPageItemCount = useMemo(() => {
    if (currentPage === calculatedTotalPages) {
      return totalBlogs - (currentPage - 1) * blogsPerPage;
    }
    return blogsPerPage;
  }, [currentPage, calculatedTotalPages, totalBlogs, blogsPerPage]);

  const getPageNumbers = () => {
    if (calculatedTotalPages <= 1) {
      return [1];
    }

    const pageNumbers = [];
    const { siblingCount, boundaryCount } = config;

    for (let i = 1; i <= boundaryCount && i <= calculatedTotalPages; i++) {
      pageNumbers.push(i);
    }

    let rangeStart = Math.max(boundaryCount + 1, currentPage - siblingCount);
    let rangeEnd = Math.min(calculatedTotalPages - boundaryCount, currentPage + siblingCount);

    const minimumPages = siblingCount * 2 + 1;
    if (rangeEnd - rangeStart + 1 < minimumPages) {
      if (currentPage <= calculatedTotalPages / 2) {
        rangeEnd = Math.min(rangeStart + minimumPages - 1, calculatedTotalPages - boundaryCount);
      } else {
        rangeStart = Math.max(rangeEnd - minimumPages + 1, boundaryCount + 1);
      }
    }

    if (rangeStart > boundaryCount + 1) {
      pageNumbers.push('...');
    }

    for (let i = rangeStart; i <= rangeEnd; i++) {
      if (!pageNumbers.includes(i)) {
        pageNumbers.push(i);
      }
    }

    if (rangeEnd < calculatedTotalPages - boundaryCount) {
      pageNumbers.push('...');
    }

    for (let i = calculatedTotalPages - boundaryCount + 1; i <= calculatedTotalPages; i++) {
      if (i > rangeEnd && i > 0 && !pageNumbers.includes(i)) {
        pageNumbers.push(i);
      }
    }

    return pageNumbers;
  };

  return (
    <Div className="flex flex-col gap-s4 md:flex-row md:items-center md:justify-between mt-s7 pb-s5">
      <Div className="text-body-cl text-cti-secondary text-center md:text-left">
        Showing {currentPageItemCount} of {totalBlogs} results
      </Div>

      <Div className="flex items-center justify-center gap-s4">
        <Button
          onClick={() => onPageChange(currentPage - 1)}
          theme="icon"
          disabled={currentPage === 1}
          className="w-s7 h-s7 flex items-center justify-center text-cti-tertiary hover:bg-cs-secondary disabled:hover:bg-transparent disabled:cursor-not-allowed focus:outline-none"
          aria-label="Previous page"
        >
          <ArrowBack className="flex items-center justify-center w-4 h-4" />
        </Button>

        <Div className="flex items-center gap-s5">
          {getPageNumbers().map((pageNum, idx) => (
            <Button
              key={idx}
              theme="icon"
              onClick={() => (typeof pageNum === 'number' ? onPageChange(pageNum) : undefined)}
              disabled={pageNum === '...'}
              className={`flex items-center justify-center text-body-base focus:outline-none ${
                pageNum === '...'
                  ? 'text-cti-tertiary cursor-default'
                  : pageNum === currentPage
                  ? 'text-cu-selected font-bold'
                  : 'text-cti-secondary hover:underline'
              }`}
            >
              {pageNum === '...' ? <MoreHoriz /> : pageNum}
            </Button>
          ))}
        </Div>

        <Button
          onClick={() => onPageChange(currentPage + 1)}
          theme="icon"
          disabled={currentPage === calculatedTotalPages}
          className="w-s7 h-s7 flex items-center justify-center text-cti-tertiary hover:bg-cs-secondary disabled:hover:bg-transparent disabled:cursor-not-allowed focus:outline-none"
          aria-label="Next page"
        >
          <ArrowForward className="flex items-center justify-center w-4 h-4" />
        </Button>
      </Div>
    </Div>
  );
};

export default memo(Pagination);
